/**
 * useKeyboardShortcuts - Reusable keyboard shortcuts hook
 * 
 * Supports cross-platform shortcuts (Cmd on Mac, Ctrl on Windows/Linux)
 */

import { useEffect, useCallback } from 'react';

export type KeyModifier = 'ctrl' | 'meta' | 'alt' | 'shift';
export type KeyCombo = {
  key: string;
  modifiers?: KeyModifier[];
  preventDefault?: boolean;
};

export interface KeyboardShortcut {
  combo: KeyCombo | KeyCombo[];  // Multiple combos for cross-platform
  handler: () => void;
  enabled?: boolean;
  description?: string;  // For help/documentation
}

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
  scope?: 'global' | HTMLElement | null;  // Where to listen
}

/**
 * Check if the current platform is Mac
 */
export function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return navigator.platform.toLowerCase().includes('mac');
}

/**
 * Create a cross-platform save shortcut (Cmd+S on Mac, Ctrl+S on Windows)
 */
export function createSaveShortcut(handler: () => void, enabled = true): KeyboardShortcut {
  return {
    combo: [
      { key: 's', modifiers: ['meta'], preventDefault: true },   // Mac: Cmd+S
      { key: 's', modifiers: ['ctrl'], preventDefault: true },   // Windows/Linux: Ctrl+S
    ],
    handler,
    enabled,
    description: 'Save file',
  };
}

/**
 * Create a cross-platform shortcut with Cmd on Mac and Ctrl on Windows
 */
export function createCrossplatformShortcut(
  key: string,
  handler: () => void,
  options?: { enabled?: boolean; description?: string; preventDefault?: boolean }
): KeyboardShortcut {
  return {
    combo: [
      { key, modifiers: ['meta'], preventDefault: options?.preventDefault ?? true },
      { key, modifiers: ['ctrl'], preventDefault: options?.preventDefault ?? true },
    ],
    handler,
    enabled: options?.enabled ?? true,
    description: options?.description,
  };
}

/**
 * Check if a key event matches a key combo
 */
function matchesCombo(event: KeyboardEvent, combo: KeyCombo): boolean {
  if (!event.key) return false;

  const modifiers = combo.modifiers || [];

  // Check key (case insensitive)
  if (event.key.toLowerCase() !== combo.key.toLowerCase()) {
    return false;
  }
  
  // Check modifiers
  const ctrlRequired = modifiers.includes('ctrl');
  const metaRequired = modifiers.includes('meta');
  const altRequired = modifiers.includes('alt');
  const shiftRequired = modifiers.includes('shift');
  
  return (
    event.ctrlKey === ctrlRequired &&
    event.metaKey === metaRequired &&
    event.altKey === altRequired &&
    event.shiftKey === shiftRequired
  );
}

/**
 * Hook to register keyboard shortcuts
 * 
 * @example
 * useKeyboardShortcuts({
 *   shortcuts: [
 *     createSaveShortcut(() => handleSave(), isDirty),
 *     createCrossplatformShortcut('z', () => handleUndo(), { description: 'Undo' }),
 *   ],
 * });
 */
export function useKeyboardShortcuts(options: UseKeyboardShortcutsOptions): void {
  const { shortcuts, enabled = true, scope = 'global' } = options;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;
    
    // Don't trigger shortcuts when typing in inputs (unless explicitly wanted)
    const target = event.target as HTMLElement;
    const isInput = target.tagName === 'INPUT' || 
                    target.tagName === 'TEXTAREA' || 
                    target.isContentEditable;
    
    // For Monaco editor, we want shortcuts to work
    const isMonaco = target.closest('.monaco-editor') !== null;
    
    for (const shortcut of shortcuts) {
      if (shortcut.enabled === false) continue;
      
      const combos = Array.isArray(shortcut.combo) ? shortcut.combo : [shortcut.combo];
      
      for (const combo of combos) {
        if (matchesCombo(event, combo)) {
          // Skip if in input and not a global shortcut like save
          if (isInput && !isMonaco && !combo.preventDefault) {
            continue;
          }
          
          // MUST prevent default and stop propagation BEFORE handler
          // to block browser's native save dialog
          if (combo.preventDefault) {
            event.preventDefault();
            event.stopPropagation();
          }
          
          shortcut.handler();
          return;
        }
      }
    }
  }, [enabled, shortcuts]);

  useEffect(() => {
    const target = scope === 'global' ? window : scope;
    if (!target) return;

    // Use capture phase to intercept before browser handles it
    target.addEventListener('keydown', handleKeyDown as EventListener, { capture: true });
    return () => {
      target.removeEventListener('keydown', handleKeyDown as EventListener, { capture: true });
    };
  }, [handleKeyDown, scope]);
}
